/* oxlint-disable confluence-feature-gating/no-preconditioning -- The master must short-circuit child gates in code to prevent child exposure in control. */
import React, { Fragment, useState, useCallback, useEffect } from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Flex from '@atlaskit/primitives/components/flex';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import Inline from '@atlaskit/primitives/inline';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import { xcss } from '@atlaskit/primitives/xcss';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import Box from '@atlaskit/primitives/box';
import Button from '@atlaskit/button/default/button';
import IconButton from '@atlaskit/button/icon/button';
import ClockIconMigration from '@atlaskit/icon/core/clock';
import Heading from '@atlaskit/heading/heading';
import CrossIcon from '@atlaskit/icon/core/cross';
import { token } from '@atlaskit/tokens';
import { isExperimentEnabled } from '@confluence/feature-experiments';

import { SidePanel, SidePanelFooter } from '@confluence/side-panel';
import { useSidePanelStateActions } from '@confluence/side-panel-state-container';
import { fg } from '@confluence/feature-gating';
import { LoadableLazy } from '@confluence/loadable';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import type { ContentTypeEventService } from '@confluence/content-types-bridge/entry-points/content-types-event-service.ts';
import { useVersionHistoryExperienceTracking } from '@confluence/content-types-utils/entry-points/useVersionHistoryExperienceTracking.ts';
import type { VersionHistoryContentType } from '@confluence/content-types-utils/entry-points/contentTypeConstants.ts';

import { useVersionHistory } from './useVersionHistory.ts';
import { useVersionHistoryAnalytics } from './useVersionHistoryAnalytics.ts';
import { RestoreContentModal } from './RestoreContentModal.tsx';
import { VersionHistoryContent } from './VersionHistoryContent.tsx';
import { supportsGroupedVersionList } from './versionHistoryUtils.ts';
import type { useVersionHistoryContentVersionHistoryQuery_contentVersionHistory_ContentVersionHistoryConnection_nodes_ContentVersion as VersionHistoryNodeType } from './__types__/useVersionHistoryContentVersionHistoryQuery.ts';
import { VersionHistoryLoadingPlaceholder } from './VersionHistoryLoadingPlaceholder.tsx';

const headerTitleStyles = xcss({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
});

// Whiteboard and database routes import this panel statically, so keep the gated list out of the
// page bundle until the panel renders it.
const VersionHistoryList = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-version-history-list" */ './VersionHistoryList.tsx'
			)
		).VersionHistoryList,
});

const i18n = defineMessages({
	versionHistoryHeaderTitle: {
		id: 'version-history.header-title',
		defaultMessage: 'Version history',
		description: 'Header title for version history component',
	},
	versionHistoryCloseButtonLabel: {
		id: 'version-history.close.button.icon.label',
		defaultMessage: 'Close',
		description:
			'The text is used as the accessible aria-label for an icon button in the version history side panel header. Clicking this button closes the version history panel.',
	},
	restoreWhiteboardLabel: {
		id: 'version-history.restore.whiteboard.label',
		defaultMessage: 'Restore as new whiteboard',
		description: 'aria label for restore as new whiteboard button',
	},
	restoreWhiteboardButtonText: {
		id: 'version-history.restore.whiteboard.button.text',
		defaultMessage: 'Restore as new whiteboard',
		description:
			'The text is shown on the primary action button in the footer of the version history side panel when the content type is a whiteboard. Clicking it opens the restore modal to save the selected version as a new whiteboard.',
	},
	restoreDatabaseButtonText: {
		id: 'version-history.restore.database.button.text',
		defaultMessage: 'Restore as new database',
		description:
			'The text is shown on the primary action button in the footer of the version history side panel when the content type is a database. Clicking it opens the restore modal to save the selected version as a new database.',
	},
	restoreDatabaseLabel: {
		id: 'version-history.restore.database.label',
		defaultMessage: 'Restore as new database',
		description: 'aria label for restore as new whiteboard button',
	},
	restoreSlideButtonText: {
		id: 'version-history.restore.slide.button.text',
		defaultMessage: 'Restore as new slide deck',
		description:
			'The text is shown on the primary action button in the footer of the version history side panel when the content type is a slide deck. Clicking it opens the restore modal to save the selected version as a new slide deck.',
	},
	restoreSlideLabel: {
		id: 'version-history.restore.slide.label',
		defaultMessage: 'Restore as new slide deck',
		description: 'aria label for restore as new slide deck button',
	},
});

const contentTypeMessaging: Record<
	VersionHistoryContentType,
	{ buttonText: MessageDescriptor; label: MessageDescriptor }
> = {
	whiteboard: {
		buttonText: i18n.restoreWhiteboardButtonText,
		label: i18n.restoreWhiteboardLabel,
	},
	database: {
		buttonText: i18n.restoreDatabaseButtonText,
		label: i18n.restoreDatabaseLabel,
	},
	slide: {
		buttonText: i18n.restoreSlideButtonText,
		label: i18n.restoreSlideLabel,
	},
};

type VersionHistoryProps = {
	headerHeight?: number;
	contentId: string;
	title: string;
	displayTopBorder?: boolean;
	panelTop?: number;
	eventService: ContentTypeEventService;
	setVersion: (version: VersionHistoryNodeType | null) => void;
	contentType: VersionHistoryContentType;
};

const VersionHistoryComponent = ({
	contentId,
	title,
	headerHeight = 0,
	displayTopBorder,
	panelTop = 0,
	eventService,
	setVersion,
	contentType,
}: VersionHistoryProps) => {
	const { hideSidePanel } = useSidePanelStateActions();
	const { formatMessage } = useIntl();
	const [showRestoreModal, setShowRestoreModal] = useState(false);

	const {
		onVersionClick,
		onVersionHistoryMenuOpen,
		onRestoreModalClose,
		onExpiredVersionsFiltered,
	} = useVersionHistoryAnalytics();

	const showGroupedList =
		supportsGroupedVersionList(contentType) &&
		isExperimentEnabled('canvas_agentic_experiences_master_exp') &&
		fg('cc-whiteboards-in-product-agent-attribution');

	const { autosavedVersions, loading, loadMoreVersions, hasNextPage, cursor, error, refetch } =
		useVersionHistory({
			contentId,
			contentType,
			includeCollaborators: showGroupedList,
			onExpiredVersionsFiltered,
		});

	// current version is selected by default
	const [selectedVersion, setSelectedVersion] = useState<VersionHistoryNodeType | null>(null);

	const { trackExperience } = useVersionHistoryExperienceTracking();

	useEffect(() => {
		// succeed side panel experience on render
		trackExperience({
			contentType,
			experience: 'sidePanel',
			task: 'succeed',
		});
	}, [contentType, trackExperience]);

	useEffect(() => {
		if (!selectedVersion) {
			setSelectedVersion(null);
		}
	}, [setSelectedVersion, selectedVersion]);

	useEffect(() => {
		onVersionHistoryMenuOpen();
	}, [onVersionHistoryMenuOpen]);

	useEffect(() => {
		return () => {
			setVersion(null);
		};
	}, [setVersion]);

	const handleClose = useCallback(() => {
		onRestoreModalClose();
		setShowRestoreModal(false);
	}, [onRestoreModalClose]);

	const handleVersionClick = useCallback(
		(version: VersionHistoryNodeType | null) => {
			setSelectedVersion(version);
			onVersionClick(version);
			setVersion(version || null);
		},
		[onVersionClick, setVersion],
	);

	// current version does not return externalVersionId
	const isRestoreButtonDisabled =
		!!error || !selectedVersion?.contentProperties?.latest?.externalVersionId;

	const handleRestoreVersionClick = useCallback(() => {
		trackExperience({
			contentType,
			experience: 'restoreModal',
			task: 'start',
		});
		setShowRestoreModal(true);
	}, [contentType, trackExperience]);

	return (
		<Fragment>
			<SidePanel
				headerHeight={headerHeight}
				displayTopBorder={displayTopBorder}
				panelTop={panelTop}
			>
				{loading ? (
					<VersionHistoryLoadingPlaceholder />
				) : (
					<Fragment>
						<Box
							backgroundColor="elevation.surface"
							paddingInlineStart="space.300"
							paddingInlineEnd="space.150"
							paddingBlock="space.150"
						>
							<Flex direction="row" justifyContent="space-between" alignItems="center">
								<Inline space="space.100" xcss={headerTitleStyles}>
									<ClockIconMigration
										label=""
										color={token('color.icon')}
										spacing="none"
										aria-hidden
									/>
									<Heading size="small" as="h5">
										<FormattedMessage {...i18n.versionHistoryHeaderTitle} />
									</Heading>
								</Inline>
								<IconButton
									appearance="subtle"
									icon={CrossIcon}
									onClick={hideSidePanel}
									label={formatMessage(i18n.versionHistoryCloseButtonLabel)}
								/>
							</Flex>
						</Box>
						{showGroupedList ? (
							<VersionHistoryList
								autosavedVersions={autosavedVersions}
								hasNextPage={hasNextPage}
								loadMoreVersions={loadMoreVersions}
								cursor={cursor}
								error={error}
								refetch={refetch}
								loading={loading}
								handleVersionClick={handleVersionClick}
								selectedVersion={selectedVersion}
								eventService={eventService}
								contentType={contentType}
								title={title}
								restoreButtonText={contentTypeMessaging[contentType].buttonText}
							/>
						) : (
							<Fragment>
								<VersionHistoryContent
									autosavedVersions={autosavedVersions}
									hasNextPage={hasNextPage}
									loadMoreVersions={loadMoreVersions}
									cursor={cursor}
									error={error}
									refetch={refetch}
									loading={loading}
									handleVersionClick={handleVersionClick}
									selectedVersion={selectedVersion}
									eventService={eventService}
									contentType={contentType}
									title={title}
								/>
								<SidePanelFooter>
									<Button
										appearance="primary"
										aria-label={formatMessage(contentTypeMessaging[contentType].label)}
										shouldFitContainer
										onClick={handleRestoreVersionClick}
										isDisabled={isRestoreButtonDisabled}
									>
										<FormattedMessage {...contentTypeMessaging[contentType].buttonText} />
									</Button>
								</SidePanelFooter>
							</Fragment>
						)}
					</Fragment>
				)}
			</SidePanel>
			{showRestoreModal && selectedVersion && (
				<RestoreContentModal
					onClose={handleClose}
					selectedVersion={selectedVersion}
					contentType={contentType}
				/>
			)}
		</Fragment>
	);
};

export const VersionHistory: React.FC<VersionHistoryProps> = withErrorBoundary({
	attribution: Attribution.CONTENT_TYPES,
})(VersionHistoryComponent);
